import { Check, Monitor, Moon, Sun } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type ThemeMode, getThemeMode, setThemeMode } from "../lib/theme.ts";

const MODES: Array<{ mode: ThemeMode; icon: typeof Sun; key: string }> = [
	{ mode: "light", icon: Sun, key: "theme.light" },
	{ mode: "dark", icon: Moon, key: "theme.dark" },
	{ mode: "system", icon: Monitor, key: "theme.system" },
];

export function ThemeToggle() {
	const { t } = useTranslation("common");
	const [open, setOpen] = useState(false);
	const [active, setActive] = useState<ThemeMode>(getThemeMode());
	const ActiveIcon = MODES.find((m) => m.mode === active)?.icon ?? Monitor;

	const pick = (mode: ThemeMode) => {
		setThemeMode(mode);
		setActive(mode);
		setOpen(false);
	};

	return (
		<div className="relative">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="inline-flex items-center justify-center rounded-lg border border-border text-foreground hover:bg-muted transition-colors size-11 sm:size-9"
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label={t("theme.label", { defaultValue: "المظهر" })}
			>
				<ActiveIcon className="size-5 sm:size-4" strokeWidth={2} aria-hidden />
			</button>
			{open && (
				<>
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop; close is reachable via the trigger button (toggles open) and the menu items */}
					<div
						className="fixed inset-0 z-20"
						onClick={() => setOpen(false)}
						aria-hidden
					/>
					<div
						role="menu"
						className="absolute z-30 mt-1 end-0 w-36 rounded-xl border border-border bg-card shadow-elevated py-1 animate-pop-in"
					>
						{MODES.map(({ mode, icon: Icon, key }) => (
							<button
								key={mode}
								type="button"
								role="menuitem"
								onClick={() => pick(mode)}
								className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-start hover:bg-muted ${mode === active ? "text-brand-700 dark:text-brand-300 font-medium" : "text-foreground"}`}
							>
								<Icon className="size-4 shrink-0" strokeWidth={2} aria-hidden />
								<span className="flex-1">{t(key, { defaultValue: mode })}</span>
								{mode === active && <Check className="size-4" aria-hidden />}
							</button>
						))}
					</div>
				</>
			)}
		</div>
	);
}
